import classNames from 'classnames';
import { forwardRef, MouseEvent, MouseEventHandler, PropsWithChildren } from 'react';
import { FaFilter, FaTimesCircle } from 'react-icons/fa';

import Button, { ButtonProps } from '../button/button';

interface FilterButtonProps extends ButtonProps {
  active?: boolean;
  onResetFilter?: MouseEventHandler<HTMLDivElement>;
  textActive?: React.ReactNode;
  textInactive?: React.ReactNode;
}

/**
 * Custom button which integrates filter state and styling
 */
export const FilterButton = forwardRef<HTMLButtonElement, PropsWithChildren<FilterButtonProps>>((props, ref) => {
  const {
    className,
    children,
    color = 'primary-dark',
    size = 'none',
    fontSize = 'sm',
    type = 'button',
    ref: _buttonRef,
    active = false,
    onResetFilter,
    textActive,
    textInactive,
    ...buttonProps
  } = props;

  const handleClickReset = (e: MouseEvent<HTMLDivElement>) => {
    if (!active) return;
    e.stopPropagation();
    if (typeof onResetFilter !== 'function') return;
    onResetFilter(e);
  };

  return (
    <Button
      className={classNames('group pl-4 pr-3 py-2', className)}
      ref={ref}
      type={type}
      color={color}
      size={size}
      fontSize={fontSize}
      {...buttonProps}
    >
      <FaFilter className={classNames('mr-5 text-md', active ? 'text-brand-primary' : 'text-gray-100')} />

      <div className="text-center">
        <span className="block leading-none mx-2">{children}</span>
        {(textActive || textInactive) && (
          <span
            className={classNames(
              'block leading-none mt-1 font-medium text-[0.625rem]',
              active ? 'text-brand-primary' : 'text-[#8b827a]',
            )}
          >
            {active ? textActive ?? '\xa0' : textInactive ?? '\xa0'}
          </span>
        )}
      </div>

      <div
        onClick={handleClickReset}
        className={classNames(
          'ml-3 p-1 rounded-full text-red-500 hover:text-red-400 active:text-red-400 opacity-0 invisible transition-opacity',
          active && 'group-hover:opacity-100 group-hover:visible',
        )}
      >
        <FaTimesCircle className="text-lg" />
      </div>
    </Button>
  );
});

export default FilterButton;
